<script>
/**
 * A grade/lista de ações do estado vazio: abas por módulo, cartões por ação.
 *
 * Substitui as pílulas de texto quando a superfície hospedeira quer mostrar o
 * repertório em vez de perguntas soltas. As pílulas diziam o que perguntar; a
 * grade/lista diz o que a IA ALCANÇA, que é a informação que faltava para quem
 * abre a tela sem saber que há Consolidador ou Compliance do lado de lá.
 *
 * Componente BURRO, mesma disciplina de `suggestion-chips` e `agent-select`:
 * não lê store nem `$access`. Quem filtra pelos módulos do escritório é o pai,
 * com `acoesVisiveis()` — aqui só sobra qual aba está aberta.
 *
 * O clique ENVIA a pergunta (evento `use` com o `prompt` inteiro), que é o mesmo
 * gesto das pílulas: o pai já liga esse evento em `enviarMensagem`. Preencher o
 * composer e esperar um segundo gesto seria um caminho novo para o mesmo fim.
 *
 * ## Ação de ATALHO — `comando`, não `prompt` (spec-001 preparacao-reuniao, [T018])
 *
 * Uma ação pode carregar `comando: {slug}` em vez de `prompt` (a home-ia
 * mescla o "módulo" de atalhos de negócio — `constants/acoes-por-modulo.js::
 * moduloDeAtalhosDeNegocio`). Esses atalhos NÃO aceitam texto livre — o
 * clique emite `usar-comando`, nunca `use`, porque o atalho só é alcançável
 * pelo campo estruturado do `/chat` (`core/atalhos/preflight.py`, "não há
 * parsing de texto digitado").
 *

 * ## Abas também no layout `lista` (10/09/2026, 2ª correção)
 *
 * O componente sempre iterou `acoesDaAba` — as ações do MÓDULO ATIVO,
 * controlado pelas abas (`grade`) ou pelo carrossel (`carrossel`); o clique
 * sempre ENVIA o `prompt` (`use`). Isso nunca mudou.
 *
 * A única mudança de hoje: as ABAS (pílulas com o nome de cada módulo — antes
 * só apareciam no layout `grade`) agora também aparecem no layout `lista`.
 * Resultado: abas (chips) acima + lista de ações do módulo ativo abaixo —
 * mesma disciplina visual do menu "+"/`/módulo`, mas com a lista vertical em
 * vez de cartões quadrados. Consumidor: a lista fixa de módulos do estado
 * vazio da `/ia` (`views/chat-imersivo/view-chat-imersivo.vue`).
 *
 * (Uma primeira tentativa desta correção fez cada LINHA da lista representar
 * um módulo inteiro, clicável para "escolher" — rejeitada pelo usuário: os
 * "chips com nome de módulo" que ele pedia eram estas abas, que já existiam.)
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */

// A aba nasce na primeira; o catálogo já vem na ordem de uso do escritório.
const PRIMEIRA_ABA = 0

// Quanto da largura visível cada clique na seta avança. Menos de 1 para o
// cartão da borda continuar à vista e o usuário não perder a referência.
const FRACAO_DA_ROLAGEM = 0.8

// Tolerância de arredondamento do `scrollLeft` — sem ela a seta do fim nunca
// desabilita, porque a conta erra por uma fração de pixel.
const FOLGA_DA_PONTA = 2

// Abaixo disso o gesto do mouse é ruído de mão (poeira do clique), não
// arrasto de verdade — o clique nativo do <button> segue disparando normal.
// Acima, é arrasto: o clique seguinte é engolido uma única vez.
const LIMIAR_DE_ARRASTO_PX = 5

// Dois tamanhos porque o `icon-svg` desenha por atributo, não por CSS: uma
// classe de variante não alcança o `width`/`height` do <svg>.
const TAMANHO_DO_ICONE = { compacta: 17, ampla: 22 }

const DENSIDADE_PADRAO = 'compacta'

// Ícone da LISTA: menor que o do cartão porque ali ele divide a linha com o
// título, em vez de encabeçar um bloco.
// [feedback do usuário, 10/09, 3ª rodada de compactação] "compactando fica
// melhor, inclusive diminuindo 1px ou 2px dos ícones" — 15 → 13, junto com o
// padding da linha (ver `.acoes-modulo__linha` no `<style>`).
// [feedback do usuário, 10/09, 4ª rodada] "agora ficou mt compacto o item,
// faça o meio termo" — 13 → 14, meio-termo entre os 15px originais e os 13px
// compactados demais.
const TAMANHO_DO_ICONE_DA_LISTA = 14

// [feedback do usuário, 10/09, 3ª correção] A lista VOLTOU a ter teto — desta
// vez por CONTAGEM, não só por altura. O `max-height`/scroll da
// `.acoes-modulo__lista` continua existindo (cobre o caso raro de ter
// expandido "mostrar mais" um módulo com muitas ações), mas em repouso só as
// primeiras `LIMITE_DE_SUGESTOES_NA_LISTA` aparecem — o resto fica atrás do
// link "Mostrar mais N sugestões". Decisão de produto: o bloco "chamava
// muita atenção" cheio de linhas; truncar por padrão devolve o foco ao chat.
const LIMITE_DE_SUGESTOES_NA_LISTA = 4

// Um elemento é "medível" quando tem layout de verdade — alguns testes
// mockam `$refs` como objeto plano (só `scrollLeft`), sem `getBoundingClientRect`.
function ehMedivel(elemento) {
  return !!elemento && typeof elemento.getBoundingClientRect === 'function'
}

// Sem seta montada (`podeRolarAbas` false daquele lado), a margem de
// segurança é 0 — não há nada cobrindo a ponta do trilho.
function larguraDaSeta(seta) {
  return ehMedivel(seta) ? seta.getBoundingClientRect().width : 0
}

// Quanto rolar pra tirar o botão de trás da seta, dos dois lados. Retorna 0
// quando o botão já está totalmente visível dentro da margem de segurança.
function deltaParaTornarVisivel({
  trilhoRect,
  botaoRect,
  margemEsquerda,
  margemDireita,
}) {
  const limiteEsquerdo = trilhoRect.left + margemEsquerda
  const limiteDireito = trilhoRect.right - margemDireita
  if (botaoRect.left < limiteEsquerdo) return botaoRect.left - limiteEsquerdo
  if (botaoRect.right > limiteDireito) return botaoRect.right - limiteDireito
  return 0
}

export default {
  name: 'IntelligenceV2AcoesPorModulo',
  props: {
    // Saída de `acoesVisiveis()`: `[{ moduleKey, rotulo, acoes: [...] }]`.
    modulos: {
      type: Array,
      default: function () {
        return []
      },
    },
    // `compacta` (padrão) ou `ampla` — ver `DENSIDADE_DA_GRADE` em
    // `views/chat-imersivo/constants.js`. Prop e não import direto: o
    // componente segue burro, e a decisão fica com a superfície hospedeira.
    // `grade` (abas + cartões em várias linhas) ou `carrossel` (sem abas, uma
    // linha só que rola). O carrossel é o do menu do "+": o módulo já está no
    // chip da caixa, então repetir as abas seria dizer a mesma coisa duas vezes.
    layout: {
      type: String,
      default: 'grade',
    },
    densidade: {
      type: String,
      default: DENSIDADE_PADRAO,
    },
    /**
     * Uma metade do bloco FALHOU — o texto que diz isso (opt-in, `''` = nada
     * a dizer, e nenhuma superfície existente muda de comportamento).
     *
     * ★ [G-R2-4 do QA da spec-001] Nasceu do catálogo de atalhos: com o
     * `GET /atalhos` abortado, a aba sumia e a tela ficava indistinguível de
     * "este escritório não tem atalhos". Quem decide a frase é a superfície
     * hospedeira (é ela que sabe O QUE falhou); este componente só a mostra
     * acima das abas, com um botão de repetir.
     *
     * ⚠️ O aviso vale para o BLOCO, não para uma aba: o módulo que falhou
     * não está na lista (é justamente o que não carregou), então não há aba
     * onde pendurá-lo.
     */
    aviso: {
      type: String,
      default: '',
    },
    // Rótulo do botão de repetir. Vazio (o default) = aviso sem ação — a
    // frase aparece sozinha, nunca um botão anônimo.
    avisoAcao: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      indiceAtivo: PRIMEIRA_ABA,
      noComeco: true,
      noFim: false,
      podeRolar: false,
      // Trilho das ABAS (chips de módulo) — estado próprio, independente do
      // carrossel de cards acima: os dois podem coexistir (abas + lista).
      noComecoAbas: true,
      noFimAbas: false,
      podeRolarAbas: false,
      arrastandoAbas: false,
      // Poeira de mão vs. arrasto de verdade: guarda de onde o gesto partiu
      // (posição do mouse e `scrollLeft` no `mousedown`) e se já cruzou o
      // limiar. `arrastoMoveu` só é lido (e resetado) no `click` da aba —
      // ver `selecionarAba`.
      origemDoArrastoAbas: null,
      arrastoMoveu: false,
      // Só vale para o layout `lista` (ver `acoesVisiveisNaLista`). Reseta a
      // cada troca de aba (`selecionarAba`) e a cada mudança de `modulos`
      // (filtro ao vivo) — "mostrar mais" é um estado da aba, não da sessão.
      mostrarTodasAsAcoesDaLista: false,
    }
  },
  computed: {
    ehCarrossel() {
      return this.layout === 'carrossel'
    },
    ehLista() {
      return this.layout === 'lista'
    },
    // Distingue o layout de cartões (grade) do de linhas (lista) — abas
    // aparecem nos dois (ver `v-if` das abas no template); só o cabeçalho de
    // carrossel é exclusivo do modo `carrossel`.
    ehGrade() {
      return !this.ehCarrossel && !this.ehLista
    },
    classeDaDensidade() {
      return ['acoes-modulo--' + this.densidade, 'acoes-modulo--' + this.layout]
    },
    tamanhoDoIcone() {
      return (
        TAMANHO_DO_ICONE[this.densidade] || TAMANHO_DO_ICONE[DENSIDADE_PADRAO]
      )
    },
    temModulos() {
      return this.modulos.length > 0
    },
    temAviso() {
      return Boolean(this.aviso)
    },
    // Com aviso e sem módulo nenhum, o bloco continua na tela: sumir seria
    // exatamente o silêncio que o aviso existe para quebrar.
    mostraBloco() {
      return this.temModulos || this.temAviso
    },
    // Índice e não `moduleKey`: se o escritório perder um módulo entre duas
    // cargas, uma chave guardada apontaria para uma aba que não existe mais e a
    // grade abriria vazia. O índice cai no clamp abaixo e mostra algo.
    moduloAtivo() {
      if (!this.temModulos) return null
      const indice = Math.min(this.indiceAtivo, this.modulos.length - 1)
      return this.modulos[indice]
    },
    tituloDaSecao() {
      return this.moduloAtivo ? this.moduloAtivo.rotulo : ''
    },
    acoesDaAba() {
      return this.moduloAtivo ? this.moduloAtivo.acoes || [] : []
    },
    // Só o layout `lista` trunca — o `grade`/`carrossel` (menu do "+")
    // continua sem teto, e `v-for` deles segue lendo `acoesDaAba` direto.
    acoesVisiveisNaLista() {
      if (this.mostrarTodasAsAcoesDaLista) return this.acoesDaAba
      return this.acoesDaAba.slice(0, LIMITE_DE_SUGESTOES_NA_LISTA)
    },
    quantidadeDeAcoesRestantesNaLista() {
      return Math.max(this.acoesDaAba.length - LIMITE_DE_SUGESTOES_NA_LISTA, 0)
    },
    temMaisAcoesNaLista() {
      return (
        !this.mostrarTodasAsAcoesDaLista &&
        this.quantidadeDeAcoesRestantesNaLista > 0
      )
    },
    // Só a CHAVE, não o índice: o `<transition>` do bloco de conteúdo usa
    // este valor para saber quando o módulo mudou de verdade (a troca de aba
    // dispara a transição pela mudança de `:key`, não pelo conteúdo do
    // `v-for`). `null` quando não há módulo ativo é um valor de key válido.
    chaveDoModuloAtivo() {
      return this.moduloAtivo ? this.moduloAtivo.moduleKey : null
    },
    // Evita chamar `ehAbaAtiva(indice)` duas vezes por aba no template — a
    // comparação já sai pronta pra cada item da grade.
    abas() {
      return this.modulos.map((modulo) => ({
        ...modulo,
        ativa: this.moduloAtivo === modulo,
      }))
    },
    tamanhoDoIconeDaLinha() {
      return this.ehLista ? TAMANHO_DO_ICONE_DA_LISTA : this.tamanhoDoIcone
    },
  },
  watch: {
    // O filtro ao vivo por texto digitado (`view-chat.vue`) pode reduzir ou
    // aumentar a quantidade de abas em runtime — sem isto `podeRolarAbas`
    // ficaria preso na medição do mount.
    modulos() {
      this.$nextTick(this.medirAbas)
      // O filtro pode trocar as ações da aba ativa por baixo do "mostrar
      // mais" já aberto — reseta para não expor um estado expandido de um
      // conjunto de ações que já não é mais o exibido.
      this.mostrarTodasAsAcoesDaLista = false
    },
  },
  mounted() {
    this.$nextTick(this.medirTrilho)
    this.$nextTick(this.medirAbas)
  },
  methods: {
    // Rola aproximadamente uma "página" de cartões. `scrollBy` e não um índice
    // controlado: a largura visível muda com a janela, e um passo fixo deixaria
    // meio cartão cortado em telas fora do tamanho previsto.
    rolar(direcao) {
      const trilho = this.$refs.trilho
      if (!trilho) return
      trilho.scrollBy({
        left: direcao * trilho.clientWidth * FRACAO_DA_ROLAGEM,
        behavior: 'smooth',
      })
      // O `scroll` é assíncrono; o estado das setas se atualiza no listener.
    },
    // Mede se o trilho transborda. Chamado na montagem e a cada rolagem — a
    // largura visível muda com a janela, e um valor calculado uma vez só
    // deixaria os chevrons errados depois de redimensionar.
    medirTrilho() {
      const trilho = this.$refs.trilho
      if (!trilho) return
      this.podeRolar = trilho.scrollWidth > trilho.clientWidth + FOLGA_DA_PONTA
      this.aoRolar()
    },
    aoRolar() {
      const trilho = this.$refs.trilho
      if (!trilho) return
      this.noComeco = trilho.scrollLeft <= FOLGA_DA_PONTA
      this.noFim =
        trilho.scrollLeft + trilho.clientWidth >=
        trilho.scrollWidth - FOLGA_DA_PONTA
    },
    selecionarAba(indice) {
      // O `click` do <button> dispara mesmo depois de um arrasto (o mouse
      // não saiu do elemento) — se o gesto cruzou o limiar, este clique é o
      // fim do arrasto, não uma escolha de aba. Consumido uma vez só: o
      // PRÓXIMO clique de verdade já reencontra a flag limpa.
      if (this.arrastoMoveu) {
        this.arrastoMoveu = false
        return
      }
      this.indiceAtivo = indice
      // Nova aba sempre recomeça truncada — "mostrar mais" é por aba, não
      // uma preferência da sessão inteira.
      this.mostrarTodasAsAcoesDaLista = false
      // A troca de classe `--ativa` (e a existência das próprias setas, que
      // mudam a margem de segurança) só assenta no DOM depois do re-render —
      // medir antes do `$nextTick` compararia contra o retângulo da aba
      // ANTIGA.
      this.$nextTick(() => {
        this.garantirAbaVisivel(indice)
      })
    },
    // Rolar até a aba escolhida ficar totalmente visível, sem ficar presa
    // atrás de uma seta (`.acoes-modulo__seta--abas`, 22px). A aba pode virar
    // ativa num ponto de scroll onde ainda está parcialmente coberta pela
    // seta esquerda ou direita — clicar nela precisa reajustar o trilho.
    garantirAbaVisivel(indice) {
      const trilho = this.$refs.trilhoAbas
      const botao = this.botaoDaAbaMedivel(indice)
      if (!ehMedivel(trilho) || !botao) return
      const delta = deltaParaTornarVisivel({
        trilhoRect: trilho.getBoundingClientRect(),
        botaoRect: botao.getBoundingClientRect(),
        margemEsquerda: larguraDaSeta(this.$refs.setaAbaAnterior),
        margemDireita: larguraDaSeta(this.$refs.setaAbaProxima),
      })
      if (delta) trilho.scrollBy({ left: delta, behavior: 'smooth' })
      // O `scrollBy` acima dispara o evento nativo `scroll` do trilho
      // (`@scroll="aoRolarAbas"`), que já reavalia `noComecoAbas`/`noFimAbas`
      // sozinho — só que em vários frames ao longo da animação suave, em vez
      // de uma vez só (era um único disparo síncrono com `scrollLeft +=`).
    },
    // O botão do índice pedido, só quando tem layout de verdade pra medir
    // (alguns testes de arrasto mockam `$refs.botaoDaAba` sem
    // `getBoundingClientRect`).
    botaoDaAbaMedivel(indice) {
      const botoes = this.$refs.botaoDaAba
      const botao = botoes && botoes[indice]
      return ehMedivel(botao) ? botao : null
    },
    // Mesmo trio rolar/medir/aoRolar do carrossel de cards, num trilho
    // PRÓPRIO: abas e carrossel podem aparecer juntos (abas + lista), e cada
    // um rola e mede o seu.
    rolarAbas(direcao) {
      const trilho = this.$refs.trilhoAbas
      if (!trilho) return
      trilho.scrollBy({
        left: direcao * trilho.clientWidth * FRACAO_DA_ROLAGEM,
        behavior: 'smooth',
      })
    },
    medirAbas() {
      const trilho = this.$refs.trilhoAbas
      if (!trilho) return
      this.podeRolarAbas =
        trilho.scrollWidth > trilho.clientWidth + FOLGA_DA_PONTA
      this.aoRolarAbas()
    },
    aoRolarAbas() {
      const trilho = this.$refs.trilhoAbas
      if (!trilho) return
      this.noComecoAbas = trilho.scrollLeft <= FOLGA_DA_PONTA
      this.noFimAbas =
        trilho.scrollLeft + trilho.clientWidth >=
        trilho.scrollWidth - FOLGA_DA_PONTA
    },
    // "Grab to scroll": guarda de onde o gesto partiu (posição do mouse e o
    // `scrollLeft` no instante do `mousedown`) para o `mousemove` aplicar só
    // a DIFERENÇA — nunca uma posição absoluta, que "saltaria" o trilho pro
    // ponto do primeiro movimento.
    iniciarArrastoAbas(evento) {
      const trilho = this.$refs.trilhoAbas
      if (!trilho) return
      this.arrastandoAbas = true
      this.arrastoMoveu = false
      this.origemDoArrastoAbas = {
        x: evento.clientX,
        scrollLeft: trilho.scrollLeft,
      }
    },
    moverArrastoAbas(evento) {
      if (!this.arrastandoAbas || !this.origemDoArrastoAbas) return
      const trilho = this.$refs.trilhoAbas
      if (!trilho) return
      const delta = evento.clientX - this.origemDoArrastoAbas.x
      if (Math.abs(delta) > LIMIAR_DE_ARRASTO_PX) this.arrastoMoveu = true
      trilho.scrollLeft = this.origemDoArrastoAbas.scrollLeft - delta
    },
    // `mouseup` (soltou o botão) e `mouseleave` (mouse saiu do trilho ainda
    // arrastando) encerram do mesmo jeito — o gesto não pode ficar "preso"
    // arrastando fora do elemento. Note que `arrastoMoveu` NÃO é limpo
    // aqui: o `click` que vem em seguida ainda precisa lê-lo.
    finalizarArrastoAbas() {
      this.arrastandoAbas = false
      this.origemDoArrastoAbas = null
    },
    usar(acao) {
      if (!acao) return
      // Atalho de negócio: NUNCA texto livre — emite o `comando` estruturado
      // e para aqui (`comando`/`prompt` são mutuamente exclusivos por
      // construção em `moduloDeAtalhosDeNegocio`).
      if (acao.comando) {
        this.$emit('usar-comando', { ...acao.comando, label: acao.titulo })
        return
      }
      if (!acao.prompt) return
      this.$emit('use', acao.prompt)
    },
    // Só expande — não há gesto de "mostrar menos" pedido; a aba já
    // recomeça truncada na próxima troca (`selecionarAba`).
    expandirListaDeAcoes() {
      this.mostrarTodasAsAcoesDaLista = true
    },
    /**
     * Entrada por TECLADO, vinda do campo do composer (seta para baixo).
     *
     * Foco itinerante — o foco sai do campo e vem para a ação — e não o
     * `aria-activedescendant` do palette. O motivo é o Enter: no campo ele
     * ENVIA a mensagem, e destacar sem mover o foco o deixaria ambíguo entre
     * enviar e executar a ação. Com o foco no `<button>`, o Enter resolve sozinho.
     *
     * ⚠️ Circula nas pontas (o `%`), menos para VOLTAR ao campo — ver
     * `aoTeclarNaAcao`.
     */
    focarAcao(indice) {
      const acoes = this.$refs.acoes || []
      if (!acoes.length) return false
      const alvo = acoes[(indice + acoes.length) % acoes.length]
      if (!alvo) return false
      // `focus()` num filho de container com overflow já o rola até a vista —
      // é o que faz o carrossel acompanhar a seta sem `scrollIntoView`.
      alvo.focus()
      return true
    },
    focarPrimeiraAcao() {
      return this.focarAcao(0)
    },
    indiceDaAcaoFocada() {
      const acoes = this.$refs.acoes || []
      return acoes.indexOf(document.activeElement)
    },
    /**
     * ⚠️ O EIXO muda com o layout: a lista empilha, então anda com ↓/↑; o
     * carrossel e a grade enfileiram, e andam com →/←. Usar ↓/↑ num carrossel
     * seria pedir para mover na direção em que nada existe.
     */
    teclasDaNavegacao() {
      if (this.ehLista) return { proxima: 'ArrowDown', anterior: 'ArrowUp' }
      return { proxima: 'ArrowRight', anterior: 'ArrowLeft' }
    },
    /**
     * ⚠️ Voltar ao campo não é "circular para o último": quem desceu por engano
     * precisa de saída, e sem ela o foco vira armadilha. Na lista é o ↑ na
     * primeira; no carrossel o ← circula e quem sai é o ↑, que ali não navega.
     * Esc sai de qualquer posição, nos dois.
     */
    aoTeclarNaAcao(evento) {
      const atual = this.indiceDaAcaoFocada()
      if (atual === -1) return
      const teclas = this.teclasDaNavegacao()

      if (evento.key === teclas.proxima) {
        evento.preventDefault()
        this.focarAcao(atual + 1)
        return
      }
      if (evento.key === teclas.anterior) {
        evento.preventDefault()
        if (this.ehLista && atual === 0) this.$emit('sair-para-o-campo')
        else this.focarAcao(atual - 1)
        return
      }
      if (evento.key === 'ArrowUp' || evento.key === 'Escape') {
        evento.preventDefault()
        this.$emit('sair-para-o-campo')
      }
    },
  },
}
</script>

<template>
  <div v-if="mostraBloco" class="acoes-modulo" :class="classeDaDensidade">
    <!-- ★ [G-R2-4] A metade que falhou aparece ANTES das abas, e diz o que
         houve — "sumiu" e "não existe" são a mesma tela para quem olha. -->
    <div v-if="temAviso" class="acoes-modulo__aviso" data-teste="acoes-modulo-aviso">
      <!-- `warning-status` é o triângulo (o mesmo do saldo baixo no rodapé
           da lateral). ⚠️ NÃO usar `warning-circle`: neste repo ele desenha
           um paper-plane, e ícone que existe mas desenha outra coisa não tem
           como avisar. -->
      <icon-svg
        name="warning-status"
        :size="14"
        color="var(--warning-text-strong)"
        class="flex-shrink-0"
      />
      <span class="acoes-modulo__aviso-texto ia-texto-auxiliar">{{ aviso }}</span>
      <button
        v-if="avisoAcao"
        type="button"
        class="acoes-modulo__aviso-acao ia-texto-auxiliar az-ds-hyperlink"
        data-teste="acoes-modulo-aviso-acao"
        @click="$emit('aviso-acao')"
      >
        {{ avisoAcao }}
      </button>
    </div>

    <!-- A barra rola na horizontal em vez de quebrar em duas linhas: com wrap,
         um escritório com muitos módulos empurraria a grade para fora da
         dobra, e a primeira coisa abaixo do composer viraria uma lista de
         abas. `role="tablist"` não entra porque isto não é um tab panel de
         verdade — não há navegação por setas nem `aria-controls`; são botões
         que trocam o conteúdo abaixo. -->
    <!-- Cabeçalho do carrossel: o nome do módulo (que as abas diriam) e os
         chevrons. Sem botão de voltar — quem desfaz a escolha é o × do chip
         na caixa, e dois caminhos para a mesma ação confundem mais que ajudam.

         ⚠️ Os chevrons só existem quando há o que rolar. Com quatro cartões
         cabendo na largura, dois botões permanentemente desabilitados seriam
         ruído anunciando algo que não existe. -->
    <div v-if="ehCarrossel" class="acoes-modulo__cabecalho">
      <span class="acoes-modulo__titulo-secao ia-texto-corpo">{{ tituloDaSecao }}</span>

      <span v-if="podeRolar" class="acoes-modulo__setas">
        <button
          type="button"
          class="acoes-modulo__seta acoes-modulo__seta--anterior"
          :disabled="noComeco"
          aria-label="Ver ações anteriores"
          @click="rolar(-1)"
        >
          <icon-svg name="arrow-down-light" :size="16" color="currentColor" />
        </button>
        <button
          type="button"
          class="acoes-modulo__seta acoes-modulo__seta--proxima"
          :disabled="noFim"
          aria-label="Ver mais ações"
          @click="rolar(1)"
        >
          <icon-svg name="arrow-down-light" :size="16" color="currentColor" />
        </button>
      </span>
    </div>

    <!-- Aba única não é escolha — é rótulo disfarçado de controle. Some com
         um módulo só (ex: o pseudo-módulo de teste do estado vazio, spec
         06/09) e volta assim que houver dois ou mais para alternar.

         ⚠️ As setas (mesma classe/rotação do carrossel de cards, §"setas")
         só existem quando `podeRolarAbas` é true — sem isso seriam dois
         botões permanentemente desabilitados anunciando algo que não existe.
         O arrasto com o mouse (grab-to-scroll) vive nos handlers do trilho
         (`.acoes-modulo__abas`), independente das setas. -->
    <div
      v-if="!ehCarrossel && modulos.length > 1"
      class="acoes-modulo__abas-wrapper"
    >
      <button
        v-if="podeRolarAbas"
        ref="setaAbaAnterior"
        type="button"
        class="acoes-modulo__seta acoes-modulo__seta--anterior acoes-modulo__seta--abas"
        :disabled="noComecoAbas"
        aria-label="Ver módulos anteriores"
        @click="rolarAbas(-1)"
      >
        <icon-svg name="arrow-down-light" :size="13" color="currentColor" />
      </button>

      <div
        ref="trilhoAbas"
        class="acoes-modulo__abas"
        :class="{ 'acoes-modulo__abas--arrastando': arrastandoAbas }"
        @scroll="aoRolarAbas"
        @mousedown="iniciarArrastoAbas"
        @mousemove="moverArrastoAbas"
        @mouseup="finalizarArrastoAbas"
        @mouseleave="finalizarArrastoAbas"
      >
        <button
          v-for="(aba, indice) in abas"
          ref="botaoDaAba"
          :key="aba.moduleKey"
          type="button"
          class="acoes-modulo__aba ia-texto-auxiliar"
          :class="{ 'acoes-modulo__aba--ativa': aba.ativa }"
          :aria-pressed="aba.ativa ? 'true' : 'false'"
          :data-texto="aba.rotulo"
          @click="selecionarAba(indice)"
        >
          <span class="acoes-modulo__aba-rotulo">{{ aba.rotulo }}</span>
        </button>
      </div>

      <button
        v-if="podeRolarAbas"
        ref="setaAbaProxima"
        type="button"
        class="acoes-modulo__seta acoes-modulo__seta--proxima acoes-modulo__seta--abas"
        :disabled="noFimAbas"
        aria-label="Ver mais módulos"
        @click="rolarAbas(1)"
      >
        <icon-svg name="arrow-down-light" :size="13" color="currentColor" />
      </button>
    </div>

    <!-- A troca de aba muda `moduloAtivo`, e é a MUDANÇA DE KEY (não o
         conteúdo do `v-for`) que dispara o fade — trocar só os itens de uma
         mesma chave não reabre `enter`/`leave` nenhum. `mode="out-in"` evita
         as duas listas convivendo um frame (lista some, então a nova entra). -->
    <transition name="acoes-modulo-troca" mode="out-in">
      <!-- LISTA: uma ação por linha, título e descrição no mesmo eixo. Sem
           cabeçalho de carrossel — mas COM as abas acima (bloco anterior),
           quando há mais de um módulo. Sem `ref="trilho"` nem `@scroll`: a
           rolagem daqui é VERTICAL e resolvida no CSS; o par `trilho`/`aoRolar`
           serve os chevrons do carrossel, que esta lista não tem. -->
      <div
        v-if="ehLista"
        :key="'lista-' + chaveDoModuloAtivo"
        class="acoes-modulo__lista"
      >
        <button
          v-for="acao in acoesVisiveisNaLista"
          ref="acoes"
          :key="acao.id"
          type="button"
          class="acoes-modulo__linha"
          @click="usar(acao)"
          @keydown="aoTeclarNaAcao"
        >
          <icon-svg
            :name="acao.icone"
            :size="tamanhoDoIconeDaLinha"
            color="currentColor"
            class="acoes-modulo__linha-icone"
          />
          <span class="acoes-modulo__linha-titulo ia-texto-corpo">{{ acao.titulo }}</span>
          <span class="acoes-modulo__linha-descricao ia-texto-auxiliar">{{
            acao.descricao
          }}</span>
        </button>

        <!-- Sem `ref="acoes"` de propósito: não é uma sugestão — a navegação
             por teclado (`focarAcao`) circula só entre as ações visíveis. -->
        <button
          v-if="temMaisAcoesNaLista"
          type="button"
          class="acoes-modulo__mostrar-mais ia-texto-auxiliar"
          @click="expandirListaDeAcoes"
        >
          Mostrar mais {{ quantidadeDeAcoesRestantesNaLista }} sugestões
        </button>
      </div>

      <div
        v-else
        ref="trilho"
        :key="'grade-' + chaveDoModuloAtivo"
        class="acoes-modulo__grade"
        @scroll="aoRolar"
      >
        <button
          v-for="acao in acoesDaAba"
          ref="acoes"
          :key="acao.id"
          type="button"
          class="acoes-modulo__cartao"
          @click="usar(acao)"
          @keydown="aoTeclarNaAcao"
        >
          <span class="acoes-modulo__icone">
            <icon-svg
              :name="acao.icone"
              :size="tamanhoDoIcone"
              color="currentColor"
            />
          </span>
          <span class="acoes-modulo__titulo ia-texto-corpo">{{ acao.titulo }}</span>
          <span class="acoes-modulo__descricao ia-texto-auxiliar">{{ acao.descricao }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
.acoes-modulo
  display: flex
  flex-direction: column
  gap: 16px
  width: 100%
  // O bloco começa mais abaixo do que o `gap` da coluna daria: o composer é o
  // centro da tela e precisa de um respiro próprio antes do repertório.
  //
  // ⚠️ Isto valia para o carrossel do módulo ESCOLHIDO no "+" (consumidor
  // OCASIONAL, que só aparece depois de um gesto) — não mais para a lista
  // fixa de módulos do estado vazio da `/ia` (consumidor PADRÃO, sempre
  // visível), que usa o modificador logo abaixo.
  margin-top: 12px

// Consumidor "lista fixa de módulos" do estado vazio da `/ia`
// (`empty-messages.vue`, branch `temAcoes`, sempre visível — não mais
// ocasional). Medido ao vivo (10/09/2026): o composer terminava em `y=537` e
// as abas em `y=581`, um vão de 44px (32px do `gap` da coluna +
// os 12px do `margin-top` acima). Pedido do usuário foi aproximar do
// composer — o `gap` de 32px da coluna (`.empty-messages__coluna`) já basta.
.acoes-modulo--proxima-do-input
  margin-top: 0

// [feedback do usuário, 3ª rodada] "pode apertar um pouco mais, coisa de 2-4px"
// — o vão de 19px (ver comentário do wrapper das abas, logo abaixo) ainda
// somava o `gap: 16px` da coluna inteira (`.acoes-modulo`), que existe para
// espaçar TODOS os blocos verticais do componente (cabeçalho do carrossel,
// abas, grade/lista). Zerado só para o layout `lista` — o único consumidor
// que empilha abas diretamente sobre a lista de sugestões (grade/carrossel
// têm o próprio `gap`, inclusive um override dedicado em
// `.acoes-modulo--carrossel` logo abaixo) — o respiro final vira só o
// `padding-bottom: 3px` do wrapper das abas.
.acoes-modulo--lista
  gap: 0

// ── abas ────────────────────────────────────────────────────────────────────
// O respiro (padding-bottom) mora no WRAPPER, não no trilho (`__abas`): com
// as setas nas pontas, é o conjunto inteiro (setas + trilho) que abre esse
// espaço em relação ao conteúdo abaixo.
//
// [feedback do usuário] a régua cinza (`border-bottom`) que fechava esse
// respiro foi removida — "chamava muita atenção". O espaço entre as abas e
// a lista/grade abaixo continua existindo (é o próprio padding), só não é
// mais desenhado como uma linha divisória.
//
// [feedback do usuário, 2ª rodada] "a lista dos chips, atualmente estão mt
// longes" — reduzido de 10px para 3px: somado ao `gap: 16px` do container
// flex (`.acoes-modulo`), o respiro total caiu de 26px para 19px.
//
// [feedback do usuário, 3ª rodada] no layout `lista` (único que empilha abas
// sobre a lista de sugestões) o `gap` do container foi zerado
// (`.acoes-modulo--lista`, acima) — o respiro total ali caiu de 19px para os
// 3px deste `padding-bottom`. O layout `grade` não foi tocado e continua com
// o vão de 19px.
//
// [feedback do usuário, 4ª rodada] "ficou mt colado a lista com o chip,
// aumente" — os 3px ficaram justos demais. Subiu para 8px (meio-termo entre
// os 3px colados e os 19px do layout `grade`), sempre somado ao `gap: 0` do
// `.acoes-modulo--lista` acima — o respiro total do layout `lista` passa a
// ser 8px.
//
// [feedback do usuário, 5ª rodada] "aumente tbm mais um pouco a distância da
// lista para os chips" — 8px → 12px.
.acoes-modulo__abas-wrapper
  display: flex
  align-items: center
  gap: 4px
  padding-bottom: 12px

.acoes-modulo__abas
  display: flex
  align-items: center
  gap: 8px
  overflow-x: auto
  flex: 1
  min-width: 0
  // "Grab to scroll": mão aberta em repouso, fechada durante o arrasto —
  // mesma linguagem do cursor nativo de qualquer painel arrastável.
  cursor: grab

  // A barra de rolagem some, mas o gesto continua: com ela visível, a régua
  // cinza de 8px encostava na borda inferior das abas e lia como um segundo
  // divisor logo acima do de verdade.
  scrollbar-width: none

  &::-webkit-scrollbar
    display: none

  &.acoes-modulo__abas--arrastando
    cursor: grabbing
    // Sem isto, arrastar rápido seleciona o texto das abas vizinhas — o
    // mesmo "rabisco" azul de qualquer drag sobre texto.
    user-select: none

.acoes-modulo__aba
  color: var(--neutral-600)
  flex-shrink: 0
  // Mais compacto (pedido do usuário, 10/09): o padding/font-size do
  // protótipo original lia "agressivo" com 7 módulos na fileira — reduzir os
  // dois também ajuda a caber mais abas antes de precisar rolar.
  padding: 5px 12px
  border: 0
  border-radius: 999px
  background: transparent
  white-space: nowrap
  cursor: pointer
  transition: background 0.15s, color 0.15s

  &:hover
    background: var(--ia-recessed-bg)
    color: var(--neutral-900)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

  // Reserva a largura do estado em negrito (600) em TODAS as abas: sem isto,
  // selecionar uma aba a engrossava, ela ficava mais larga e empurrava as
  // vizinhas (print do usuário, 13/09: "Comissões" deslocou a fileira). Um
  // clone invisível do rótulo, já em bold e com altura zero, faz o botão
  // nascer na largura final — trocar o peso não move mais nada.
  &::after
    content: attr(data-texto)
    display: block
    height: 0
    overflow: hidden
    visibility: hidden
    font-weight: 600
    pointer-events: none

// Sem a pílula preta sólida (decisão do usuário, 13/09): a fileira inteira fica
// mais discreta. A ativa não tem fundo nenhum em repouso — o contraste contra
// as vizinhas vem só do peso (600) + texto escuro, já que as não ativas ficam
// apagadas (n600). O cinza `--ia-recessed-bg` é exclusivo do hover, em todas
// as abas, pra pílula seguir sendo sinal de "alvo do mouse" e não de estado.
.acoes-modulo__aba--ativa
  color: var(--neutral-900)
  font-weight: 600

  &:hover
    background: var(--ia-recessed-bg)
    color: var(--neutral-900)

// ── grade ───────────────────────────────────────────────────────────────────
// `auto-fill` com mínimo de 200px em vez de contagem fixa de colunas: a mesma
// grade serve a coluna de 672px do estado vazio e a tela cheia da imersiva sem
// media query, e o último cartão de uma linha incompleta não estica.
.acoes-modulo__grade
  display: grid
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr))
  gap: 4px

// ★ EM REPOUSO O CARTÃO NÃO É UMA CAIXA.
//
// Na primeira versão ele era branco com borda — e o composer logo acima também
// é branco com borda. Seis cartões assim viravam sete retângulos disputando o
// olho, e o chat, que é o centro da tela, perdia a primazia (revisão de
// 26/08). O que rouba destaque é CONTRASTE, não área: recuar a superfície
// devolve o foco sem esconder o repertório.
//
// A caixa só existe no hover, quando o cartão de fato virou o alvo.
.acoes-modulo__cartao
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 6px
  padding: 12px
  border: 1px solid transparent
  border-radius: 12px
  background: transparent
  text-align: left
  cursor: pointer
  transition: transform 0.18s ease-out, border-color 0.18s, background 0.18s, box-shadow 0.18s

  &:hover
    transform: translateY(-3px)
    border-color: var(--primary)
    background: var(--white)
    // Sombra AMBIENTE — raio grande e opacidade baixa, tingida com o texto e
    // não com preto puro, que sujaria o cinza quente da tela.
    box-shadow: 0 10px 20px -12px rgba(39, 39, 42, 0.18)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

  // O quadrado do ícone acende junto com a borda: a receita é a MESMA do chip
  // de escopo com cliente (`escopo-select.vue`), que já é o "primary light"
  // deste módulo. Um segundo jeito de dizer "primary" faria duas famílias.
  &:hover .acoes-modulo__icone
    background: color-mix(in srgb, var(--primary) 10%, var(--white))
    color: var(--primary)

.acoes-modulo__icone
  display: inline-flex
  align-items: center
  justify-content: center
  width: 30px
  height: 30px
  border: 0
  border-radius: 9px
  background: var(--ia-recessed-bg)
  color: var(--neutral-700)
  transition: background 0.18s, color 0.18s

// ── variante AMPLA ──────────────────────────────────────────────────────────
// O desenho da primeira versão, byte a byte, atrás de `DENSIDADE_DA_GRADE`.
// Fica aqui em vez de morrer no histórico porque a escolha entre "mostrar o
// repertório com presença" e "manter o chat no centro" é de produto, não de
// implementação — e trocar uma constante é mais barato que reescrever.
.acoes-modulo--ampla
  .acoes-modulo__grade
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))
    gap: 12px

  .acoes-modulo__cartao
    gap: 8px
    padding: 18px
    border-color: var(--neutral-200)
    border-radius: 14px
    background: var(--white)

    &:hover
      transform: translateY(-4px)
      box-shadow: 0 12px 24px -12px rgba(39, 39, 42, 0.18)

  .acoes-modulo__icone
    width: 40px
    height: 40px
    margin-bottom: 4px
    border: 1px solid var(--neutral-200)
    border-radius: 11px
    background: var(--neutral-100)

  .acoes-modulo__cartao:hover .acoes-modulo__icone
    border-color: transparent

  .acoes-modulo__titulo
    font-size: var(--ia-font-corpo)

  .acoes-modulo__descricao
    display: block
    font-size: var(--ia-font-auxiliar)
    line-height: 1.45

// ★ --neutral-700 e NÃO --neutral-600. O contraste medido em
// `design/_ia-block-card.sass`: o 600 passa em branco (4.83) mas reprova AA
// sobre --neutral-100 (4.40) — e este texto convive com o quadrado cinza logo
// acima. O 700 dá 7.73 nas duas superfícies.
.acoes-modulo__descricao
  color: var(--neutral-700)
  // Teto de duas linhas: sem ele, a ação de texto mais longo define a altura de
  // TODA a linha da grade, e uma frase a mais devolvia o bloco ao tamanho de
  // antes. O corte é raro — as descrições do catálogo cabem em duas.
  display: -webkit-box
  -webkit-line-clamp: 2
  -webkit-box-orient: vertical
  overflow: hidden

// Vão entre os cartões do carrossel. Variável porque a largura de cada um é
// derivada dele — mudar num lugar só desalinha a conta.
$vao-do-carrossel: 10px

// ── variante CARROSSEL ──────────────────────────────────────────────────────
// Uma linha só que rola na horizontal, com cabeçalho e setas. É a forma que o
// menu do "+" abre: o módulo já está no chip da caixa, então o espaço aqui é
// para as ações, não para repetir o nome dele em abas.
.acoes-modulo--carrossel
  gap: 10px

  .acoes-modulo__grade
    // `flex` e não `grid`: no carrossel os cartões têm largura fixa e seguem
    // em fila, em vez de se distribuírem pela largura disponível.
    display: flex
    flex-wrap: nowrap
    gap: $vao-do-carrossel
    overflow-x: auto
    // A barra some, mas o gesto de arrastar continua — as setas são um
    // atalho, não o único caminho.
    scrollbar-width: none
    // Respiro para o `translateY(-3px)` do hover não ser cortado pelo overflow.
    padding: 4px 0

    &::-webkit-scrollbar
      display: none

  .acoes-modulo__cartao
    // ⚠️ Largura DERIVADA, não fixa: com 200px fixos, quatro cartões mais os
    // vãos davam 830px num trilho de 768 e o último nascia cortado — mesmo
    // quando não havia o que rolar. A conta reserva os três vãos e divide o
    // resto, então quatro cabem exatos e o quinto (quando existe) fica fora,
    // que é o convite a rolar.
    flex: 0 0 calc((100% - #{$vao-do-carrossel * 3}) / 4)
    min-width: 150px
    // `scroll-snap` alinha o cartão à borda esquerda depois da inércia, senão
    // a rolagem para no meio de um deles.
    scroll-snap-align: start

.acoes-modulo__cabecalho
  display: flex
  align-items: center
  gap: 8px

.acoes-modulo__titulo-secao
  color: var(--neutral-900)
  flex: 1
  min-width: 0

.acoes-modulo__seta
  display: inline-flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 28px
  height: 28px
  border: 0
  border-radius: 50%
  background: transparent
  color: var(--neutral-700)
  cursor: pointer
  transition: background 0.15s, color 0.15s, opacity 0.15s

  &:hover:not(:disabled)
    background: var(--neutral-100)
    color: var(--neutral-900)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 1px

  // Desabilitada e não escondida: some com a seta e as duas dançam de lugar a
  // cada rolagem.
  &:disabled
    opacity: 0.35
    cursor: default

// Setas das abas de módulo: mesma anatomia acima, um pouco menores — pedido
// pontual do usuário ("diminua o tamanho das flechas, um pouco só"). Não
// afeta as setas do carrossel de cards (--anterior/--proxima sem --abas).
.acoes-modulo__seta--abas
  width: 22px
  height: 22px

.acoes-modulo__setas
  display: inline-flex
  align-items: center
  gap: 4px

// ⚠️ O set NÃO tem chevron horizontal, e o `arrow-left` dele aponta para a
// DIREITA (medido no browser — foi o que deixou as setas invertidas na primeira
// tentativa). O caminho confiável é girar o chevron de baixo: -90° aponta para
// a esquerda, +90° para a direita. Nada de espelhar `arrow-left`.
.acoes-modulo__seta--anterior ::v-deep svg
  transform: rotate(-90deg)

.acoes-modulo__seta--proxima ::v-deep svg
  transform: rotate(90deg)

// ---------------------------------------------------------------------------
// Layout LISTA — uma ação por linha, abaixo do composer.
//
// Sem cartão e sem superfície em repouso: a linha é lida como texto, e só o
// hover a materializa. É o oposto do carrossel, onde cada ação é um objeto —
// aqui elas são um índice do que dá para perguntar.
// ---------------------------------------------------------------------------
.acoes-modulo__lista
  display: flex
  flex-direction: column
  width: 100%
  // Teto de ALTURA, não de itens: todas as ações continuam alcançáveis, e o
  // composer não se move por causa da quantidade. Recalibrado (10/09/2026,
  // medido ao vivo): a aba CRM, hoje o maior catálogo (7 ações), media
  // `scrollHeight: 273px` contra os `260px` antigos — só 13px de sobra, o que
  // já criava scroll quase sempre. 320px cobre o CRM inteiro sem scroll e dá
  // folga para catálogos que cresçam, sem competir demais com a dobra.
  max-height: 320px
  overflow-y: auto
  // Sem `background` próprio: a lista tem de herdar a superfície do chat. Com
  // cor aqui ela viraria uma placa branca sobre o fundo quase-branco — o mesmo
  // defeito que o cartão de cliente em foco teve.

  // ⚠️ Trilho TRANSPARENTE. A regra global (`design/index.sass`) pinta o trilho
  // de `--neutral-50`, que nasceu para o branco da plataforma; sobre a
  // superfície do chat ele vira uma faixa clara colada na borda. Mesma receita
  // da lateral (`chat-sidebar.vue`), inclusive o polegar na família dela.
  &::-webkit-scrollbar-track
    background: transparent

  &::-webkit-scrollbar-thumb
    background: var(--sidebar-warm-border)
    box-shadow: none

    &:hover
      background: var(--neutral-300)

  // Firefox não tem os pseudo-elementos acima; `scrollbar-color` é o
  // equivalente (polegar, trilho).
  scrollbar-width: thin
  scrollbar-color: var(--sidebar-warm-border) transparent

.acoes-modulo__linha
  display: flex
  align-items: baseline
  gap: 12px
  width: 100%
  // Alvo de clique maior: a linha é o item principal desta tela, e com 8px ela
  // lia como opção de menu em vez de sugestão para clicar.
  //
  // [feedback do usuário, 10/09, 3ª rodada] "acho que tem mt padding,
  // compactando fica melhor" — 12px 14px → 8px 10px. Não voltou aos 8px
  // ISOTRÓPICOS do comentário acima (aquilo lia como menu): aqui o corte é
  // proporcional nos dois eixos, e o vertical continua abaixo do horizontal.
  //
  // [feedback do usuário, 10/09, 4ª rodada] "agora ficou mt compacto o item,
  // faça o meio termo" — 8px 10px → 10px 12px, meio-termo entre os 12px 14px
  // originais e os 8px 10px compactados demais.
  padding: 10px 12px
  border: 0
  background: none
  border-radius: 12px
  text-align: left
  cursor: pointer
  transition: background var(--motion-duration-fast) var(--motion-easing-standard)

  &:hover,
  &:focus
    background: var(--neutral-100)

// `align-self` e não `align-items` na linha: o ícone alinha pelo TOPO do texto,
// e a linha inteira usa `baseline` para título e descrição assentarem juntos.
.acoes-modulo__linha-icone
  flex-shrink: 0
  align-self: center
  color: var(--neutral-400)

// Tipografia do menu do "+": os papéis `ia-texto-corpo` (título) e
// `ia-texto-auxiliar` (descrição) de `design/_ia-texto.sass`, aplicados no
// template; aqui ficam o layout e a COR. O peso do título é o do papel
// (400): o 600 "chamava muita atenção" e foi a 500 na leva 1, ao papel na 2.
.acoes-modulo__linha-titulo
  color: var(--neutral-900)
  flex-shrink: 0
  white-space: nowrap

.acoes-modulo__linha-descricao
  color: var(--neutral-600)
  flex: 1 1 auto
  min-width: 0
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

// Rótulo de ação, não sugestão — mesma receita do "Ver todas" da lateral de
// conversas (`.conversations-ver-todas`): sem cromo de botão, sublinha só no
// hover, cor neutra para não competir com os títulos das sugestões.
.acoes-modulo__mostrar-mais
  color: var(--neutral-600)
  display: block
  width: 100%
  background: none
  border: none
  padding: 10px 14px
  cursor: pointer
  text-align: left

  &:hover,
  &:focus
    text-decoration: underline
    color: var(--neutral-900)

// Troca de aba: fade + deslocamento vertical curto, sutil o bastante para não
// competir com o clique que a disparou. Só `opacity`/`transform`
// (GPU-compositáveis) — nunca propriedade que force layout, mesmo aviso já
// registrado no CLAUDE.md do repo para `v-expand-transition`.
.acoes-modulo-troca-enter-active,
.acoes-modulo-troca-leave-active
  transition: opacity 0.15s ease, transform 0.15s ease

.acoes-modulo-troca-enter
  opacity: 0
  transform: translateY(4px)

.acoes-modulo-troca-leave-to
  opacity: 0
  transform: translateY(-4px)

@media (prefers-reduced-motion: reduce)
  .acoes-modulo-troca-enter-active,
  .acoes-modulo-troca-leave-active
    transition: none

  .acoes-modulo-troca-enter,
  .acoes-modulo-troca-leave-to
    opacity: 1
    transform: none

// ★ [G-R2-4] A faixa do aviso: discreta (isto não é o conteúdo do bloco),
// mas presente — o defeito que ela corrige era a AUSÊNCIA de qualquer marca
// na tela. Tons semânticos de aviso, nunca hex.
.acoes-modulo__aviso
  display: flex
  align-items: center
  gap: 6px
  margin-bottom: 8px
  padding: 6px 10px
  border-radius: var(--radius-sm)
  // `--warning-bg-soft` + `--warning-text-strong`: o par documentado em
  // `_controls.sass`. O `--warning-text` (#B36A00) NÃO alcança AA sobre este
  // tint (3.52 medido, contra o mínimo de 4.5) — o gêmeo forte existe só por
  // causa disso.
  background: var(--warning-bg-soft)

.acoes-modulo__aviso-acao
  flex-shrink: 0
  margin-left: auto
  background: none
  border: 0
  cursor: pointer

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.acoes-modulo__titulo
  color: var(--neutral-900)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.acoes-modulo__aviso-texto
  color: var(--warning-text-strong)

</style>
